import React, { ReactText } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  persistTemporaryItem,
  renameFile,
  renameFolder,
  // @ts-ignore
} from 'store/services/flow/actions';
import { TreeItem } from 'components/GenericTree';
import { Input } from './styled';
import Text from './Text';
import { useTreeActions } from '../TreeActionsContext';
import { getPath } from '../utils';

const SContainer = styled.div<{ width?: number }>`
  overflow: hidden;
  width: ${({ width }) => (width ? `${width}px` : 'auto')};

  // The following rule targets the text cropper's <a> tag to remove the outline.
  // This is done to override a rule set by foundation.
  & > [data-test='text-cropper'] {
    a:focus {
      outline: none;
    }
  }
`;

interface RenameParams {
  newName: string;
  id: ReactText;
}

interface Props {
  match?: string;
  item: TreeItem;
  name: string;
  exitEditMode: () => void;
  onBlur: () => void;
  setName: (input: string) => void;
  renameFile: (params: RenameParams) => Promise<void>;
  renameFolder: (params: RenameParams) => Promise<void>;
  persistTemporaryItem: (item: object) => Promise<void>;
  width?: number;
}

const TextInput = ({
  match = '',
  item,
  name,
  exitEditMode,
  onBlur,
  renameFile,
  renameFolder,
  persistTemporaryItem,
  setName,
  width,
}: Props) => {
  const { rename } = useTreeActions();

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = event =>
    setName(event.target.value);

  async function persist(text: string) {
    if (!item.isPersisted) {
      exitEditMode();
      setName(text);
      await persistTemporaryItem({
        path: getPath(item.id),
        name: text,
        isFolder: item.isFolder,
      });
    } else {
      try {
        const blob = { id: item.id, newName: text };
        await (item.isFolder ? renameFolder(blob) : renameFile(blob));
        rename(item.id, text);
      } catch {
        setName(item.data.title);
      } finally {
        exitEditMode();
      }
    }
  }

  const handleBlur = async (value: string) => {
    onBlur();

    if (!item.isPersisted || (value !== '' && value !== item.data.title)) {
      await persist(value);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<
    HTMLInputElement
  > = async event => {
    const text = (event.target as HTMLInputElement).value;

    if (event.key === 'Escape' && item.isPersisted) {
      exitEditMode();
    } else if (
      (event.key === 'Escape' && !item.isPersisted) ||
      (event.key === 'Enter' && text !== '')
    ) {
      await handleBlur(text);
    }
  };

  // NOTE: Padding the prop width here is a way to make the component re-render.
  // What we care about is the contained text cropper. Notice, that this width
  // is also passed as the `key` prop in the Text component.
  // This is done to correctly work when the splitter, a parent component, is
  // resized.
  return (
    <SContainer width={width}>
      {item.isBeingEdited ? (
        <Input
          autoFocus
          data-test="file-name-input"
          placeholder="Item name"
          value={name}
          onBlur={e => handleBlur(e.target.value)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <Text
          key={width}
          match={match}
          text={item.data ? item.data.title : ''}
        />
      )}
    </SContainer>
  );
};

export default connect(
  null,
  { renameFile, renameFolder, persistTemporaryItem },
)(TextInput);
